import { ImageFile } from '@/app/editor/components/layers/image-layer/upload-image-tool';
import { env } from './env';
import { Cart, CartItem } from '@shop/models/src/cart';
import { Product } from '@shop/models/src/product';
import { State } from '@shop/utils/product-state';
import { matchCartItem } from '@shop/utils/cart.js';
import obj from '@shop/utils/obj';

const storageKeys = {
  mobile: 'cshop-mobile',
  address: 'cshop-address',
  user: 'cshop-user',
  token: 'cshop-token',
  uploads: 'cshop-uploads',
  cart: 'cshop-cart',
} as const;
type StorageKeys = keyof typeof storageKeys;

const meta = {
  cart: {
    json: true,
    get default() {
      return { items: [] };
    },
    utils: (api: StorageApi<Cart>) => ({
      removeItem(product: Product, project: Product['project'], props: State) {
        const cart = api.get();
        cart.items = cart.items.filter(
          (x) =>
            !matchCartItem(
              [x.productId.toString(), product.id],
              [x.projectId.toString(), project.id],
              [x.props, props],
            ),
        );
        api.set(cart);
        return { ...cart };
      },
      addItem(
        product: Product,
        project: Product['project'],
        props: State,
        newItem: CartItem,
      ) {
        const cart = api.get();
        const item = cart.items.find((x) =>
          matchCartItem(
            [x.productId.toString(), product.id],
            [x.projectId.toString(), project.id],
            [x.props, props],
          ),
        );
        if (item != null) {
          throw 'item already exists!';
        }
        cart.items.push(newItem);
        api.set(cart);
        return { ...cart };
      },
      hasItem(product: Product, project: Product['project'], props: State) {
        const cart = api.get();
        return !!cart.items.some((x) =>
          matchCartItem(
            [x.productId.toString(), product.id],
            [x.projectId.toString(), project.id],
            [x.props, props],
          ),
        );
      },
      updateItem(
        product: Product,
        project: Product['project'],
        props: State,
        updatedItem: Partial<CartItem>,
      ) {
        const cart = api.get();
        const idx = cart?.items.findIndex((x) =>
          matchCartItem(
            [x.productId.toString(), product.id],
            [x.projectId.toString(), project.id],
            [x.props, props],
          ),
        );
        if (idx < 0) {
          throw 'item does not exist!';
        }
        cart.items.splice(idx, 1, {
          ...cart.items[idx],
          ...(updatedItem as CartItem),
        });
        api.set(cart);
        return { ...cart };
      },
    }),
  },
  user: {
    json: true,
    default: {},
    utils: () => ({}),
  },
  uploads: {
    json: true,
    get default() {
      return [];
    },
    utils: (api: StorageApi<ImageFile[]>) => ({
      append(item: ImageFile) {
        let uploads = api.get();
        uploads.push(item);
        api.set(uploads);
      },
    }),
  },
};
type Meta = typeof meta;

const methods = ['get', 'set', 'remove'] as const;
type StorageApi<V = any> = {
  get<T = V>(opts?: { json: boolean }): T;
  set(value: V, opts?: { json: boolean }): void;
  remove(): void;
};
type StorageKeyMethodMap<Key extends StorageKeys> = StorageApi &
  (Key extends keyof Meta ? ReturnType<Meta[Key]['utils']> : {});

function factory(persistor: Storage) {
  return obj.keys(storageKeys).reduce(
    (acc, key) => {
      const storageKey = storageKeys[key];
      const _meta = meta[key];
      const handlers = methods.reduce(
        (a, c) => {
          switch (c) {
            case 'get': {
              // TODO:
              // if (env.isSSR())
              //   throw new Error("We should not use this api on server side");
              a[c] = function <T = any>(
                { json } = { json: _meta?.json ?? false },
              ): T {
                const r = persistor['getItem'](storageKey);
                return !!r && json ? JSON.parse(r) : _meta?.default ?? r;
              };
              return a;
            }

            case 'set': {
              a[c] = (
                value: any,
                { json } = { json: _meta?.json ?? false },
              ) => {
                persistor['setItem'](
                  storageKey,
                  json ? JSON.stringify(value) : value,
                );
              };
              return a;
            }

            case 'remove': {
              a[c] = () => persistor['removeItem'](storageKey);
              return a;
            }
          }
        },
        {} as StorageKeyMethodMap<typeof key>,
      );

      if (_meta?.utils) {
        const utils = _meta.utils(handlers);
        for (const util of Object.keys(utils)) {
          handlers[util] = utils[util as keyof typeof utils] as any;
        }
      }

      acc[key] = handlers as any;
      return acc;
    },
    {} as { [K in StorageKeys]: StorageKeyMethodMap<K> },
  );
}

const persistors = ['local', 'session'] as const;
type Persistors = (typeof persistors)[number];
type StorageObj = Record<Persistors, ReturnType<typeof factory>> & {
  remove(keys: StorageKeys[]): void;
};

const storage = persistors.reduce((a, persistor) => {
  a[persistor] = factory(
    !env.isSSR()
      ? window[`${persistor}Storage`]
      : ({ getItem() {}, setItem() {}, removeItem() {} } as any),
  );
  return a;
}, {} as StorageObj);

storage.remove = (keys: StorageKeys[]) => {
  keys.forEach((key) => {
    persistors.forEach((persistor) => {
      window[`${persistor}Storage`].removeItem(storageKeys[key]);
    });
  });
};

export { storage };
